import SimpleTooltip from "@rio-cloud/rio-uikit/SimpleTooltip";
import moment from "moment";
import { ReactNode } from "react";

import { useT } from "@hooks";

import type { KpiComparison, KpiComparisonPeriod } from "@types";
import type { KpiTooltipPlacement } from "./KpiInfoTooltip";

const formatPeriod = (period?: KpiComparisonPeriod | null): string | null => {
  const start = moment(period?.startDate ?? "", "YYYY-MM-DD", true);
  const end = moment(period?.endDate ?? "", "YYYY-MM-DD", true);

  if (!start.isValid() || !end.isValid()) return null;

  return `${start.format("DD/MM")} - ${end.format("DD/MM/YYYY")}`;
};

export const formatVariationPercent = (value?: number | null): string => {
  if (value === undefined || value === null || Number.isNaN(value)) return "-";

  const rounded = Number(value.toFixed(2));

  return `${rounded.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
};

interface KpiComparisonContentProps {
  comparison: KpiComparison;
  formatValue: (value: number) => string;
}

const KpiComparisonContent = ({ comparison, formatValue }: KpiComparisonContentProps) => {
  const t = useT();
  const currentPeriod = formatPeriod(comparison.currentPeriod);
  const previousPeriod = formatPeriod(comparison.previousPeriod);

  return (
    <div className="display-flex flex-column gap-10">
      <span className="text-medium">{t("kpi.comparison.title")}</span>

      {(currentPeriod || previousPeriod) && (
        <div className="display-flex flex-column">
          {currentPeriod && <span>{t("kpi.comparison.currentPeriod", { period: currentPeriod })}</span>}
          {previousPeriod && <span>{t("kpi.comparison.previousPeriod", { period: previousPeriod })}</span>}
        </div>
      )}

      <div className="bg-darker text-color-white rounded padding-5 padding-x-10 display-flex flex-column">
        <span>{t("kpi.comparison.currentValue", { value: formatValue(comparison.currentValue) })}</span>
        <span>{t("kpi.comparison.previousValue", { value: formatValue(comparison.previousValue) })}</span>
        <span>{t("kpi.comparison.variation", { value: formatVariationPercent(comparison.variationPercent) })}</span>
      </div>
    </div>
  );
};

interface KpiComparisonTooltipProps {
  comparison?: KpiComparison | null;
  formatValue: (value: number) => string;
  placement?: KpiTooltipPlacement;
  children: ReactNode;
}

const KpiComparisonTooltip = ({
  comparison,
  formatValue,
  placement = "bottom-start",
  children,
}: KpiComparisonTooltipProps) => {
  if (!comparison) return <>{children}</>;

  return (
    <SimpleTooltip
      content={<KpiComparisonContent comparison={comparison} formatValue={formatValue} />}
      placement={placement}
      width={250}
      textAlignment="left"
      innerClassName="bg-darkest text-color-white kpi-tooltip"
    >
      <span className="display-flex flex-wrap align-items-center gap-5 cursor-pointer">{children}</span>
    </SimpleTooltip>
  );
};

export default KpiComparisonTooltip;
